import { createFileRoute } from '@tanstack/react-router'
import { BarChart3 } from 'lucide-react'

import { ExportButton } from '@/components/common/export-button'
import { useLeaveStatistics, type LeaveType, type LeaveStatus } from '@/hooks'

export const Route = createFileRoute('/_authenticated/hr/leaves/statistics')({
  component: LeaveStatisticsPage,
})

const TYPE_LABELS: Record<LeaveType, string> = {
  annual: '年假',
  sick: '病假',
  personal: '事假',
  marriage: '婚假',
  maternity: '产假',
  paternity: '陪产假',
  bereavement: '丧假',
}

const STATUS_LABELS: Record<LeaveStatus, string> = {
  draft: '草稿',
  pending: '待审批',
  approved: '已批准',
  rejected: '已拒绝',
  cancelled: '已取消',
}

function LeaveStatisticsPage() {
  const currentYear = new Date().getFullYear()
  const { data: statistics, isLoading, error } = useLeaveStatistics({ year: currentYear })

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-destructive">加载失败: {error.message}</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">请假统计</h1>
          <p className="text-muted-foreground">{currentYear} 年请假数据统计</p>
        </div>
        <ExportButton
          type="leaves"
          filters={{ year: currentYear }}
        />
      </div>

      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="text-muted-foreground">加载中...</div>
        </div>
      ) : !statistics ? (
        <div className="flex h-64 flex-col items-center justify-center gap-4 rounded-lg border border-dashed">
          <BarChart3 className="h-12 w-12 text-muted-foreground" />
          <p className="text-muted-foreground">暂无统计数据</p>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {/* 按类型统计 */}
          <div className="rounded-lg border p-4">
            <h3 className="mb-4 font-semibold">按类型统计</h3>
            <div className="space-y-3">
              {statistics.byType.map((item) => (
                <div key={item.type} className="flex items-center justify-between">
                  <span className="text-sm">{TYPE_LABELS[item.type]}</span>
                  <div className="text-right">
                    <span className="font-medium">{item.count}</span>
                    <span className="text-sm text-muted-foreground"> 次 / </span>
                    <span className="font-medium">{item.days}</span>
                    <span className="text-sm text-muted-foreground"> 天</span>
                  </div>
                </div>
              ))}
              {statistics.byType.length === 0 && (
                <p className="text-sm text-muted-foreground">暂无数据</p>
              )}
            </div>
          </div>

          {/* 按状态统计 */}
          <div className="rounded-lg border p-4">
            <h3 className="mb-4 font-semibold">按状态统计</h3>
            <div className="space-y-3">
              {statistics.byStatus.map((item) => (
                <div key={item.status} className="flex items-center justify-between">
                  <span className="text-sm">{STATUS_LABELS[item.status]}</span>
                  <span className="font-medium">{item.count} 次</span>
                </div>
              ))}
              {statistics.byStatus.length === 0 && (
                <p className="text-sm text-muted-foreground">暂无数据</p>
              )}
            </div>
          </div>

          {/* 按月份统计 */}
          <div className="rounded-lg border p-4 md:col-span-2 lg:col-span-1">
            <h3 className="mb-4 font-semibold">按月份统计</h3>
            <div className="space-y-3">
              {statistics.byMonth.map((item) => (
                <div key={item.month} className="flex items-center justify-between">
                  <span className="text-sm">{item.month}</span>
                  <div className="text-right">
                    <span className="font-medium">{item.count}</span>
                    <span className="text-sm text-muted-foreground"> 次 / </span>
                    <span className="font-medium">{item.days}</span>
                    <span className="text-sm text-muted-foreground"> 天</span>
                  </div>
                </div>
              ))}
              {statistics.byMonth.length === 0 && (
                <p className="text-sm text-muted-foreground">暂无数据</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
